import { useEffect, useRef, useState } from 'react';
import { getStreamByEndpointAsync } from 'services/Events';
import { getOccurrenceStreamEndpoint } from 'services/OccurrenceService';
import { ICriticalAlertPayload } from 'services/OccurrenceService/types';

interface UseCriticalOccurrenceSocketProps {
  onAlertReceived?: (payload: ICriticalAlertPayload) => void;
  enabled?: boolean;
}

export const useCriticalOccurrenceSocket = ({
  onAlertReceived,
  enabled = true,
}: UseCriticalOccurrenceSocketProps = {}) => {
  const [latestAlert, setLatestAlert] = useState<ICriticalAlertPayload | null>(null);

  const onAlertReceivedRef = useRef(onAlertReceived);
  useEffect(() => {
    onAlertReceivedRef.current = onAlertReceived;
  }, [onAlertReceived]);

  useEffect(() => {
    if (!enabled) return;

    let isActive = true;
    let socketConnection: WebSocket | null = null;

    const fetchData = async () => {
      const endpoint = getOccurrenceStreamEndpoint();

      const connection = await getStreamByEndpointAsync(
        endpoint,
        (payload: ICriticalAlertPayload) => {
          if (!isActive) return;

          // A primeira mensagem após conectar/reconectar é sempre o handshake do backend
          // ({ message: "Conectado..." }, sem occurrenceId) — o filtro abaixo já a ignora
          // pelo conteúdo. Não descartar por posição: se um alerta real for a primeira
          // mensagem após uma reconexão (queda de rede, restart do backend), ele não pode
          // ser perdido.
          if (payload && payload.occurrenceId) {
            console.warn(`🚨 ALERTA CRÍTICO: Ocorrência #${payload.occurrenceId} - ${payload.personName}`);
            setLatestAlert(payload);
            
            if (onAlertReceivedRef.current) {
              onAlertReceivedRef.current(payload);
            }
          }
        },
        { pageNumber: 1, pageSize: 1 },
        () => {},
        () => {},
        { isPaused: false } as any,
        false,
        undefined,
        false,
        () => {},
        undefined,
        0
      );

      if (!isActive) {
        if (connection) {
          (connection as any)._isIntentionalClose = true;
          connection.close();
        }
        return;
      }

      socketConnection = connection;
    };

    fetchData();

    return () => {
      isActive = false;
      if (socketConnection) {
        (socketConnection as any)._isIntentionalClose = true;
        socketConnection.close();
      }
    };
  }, [enabled]);

  const clearAlert = () => setLatestAlert(null);

  return {
    latestAlert,
    clearAlert,
  };
};